import { useState } from "react";
import { BadgeCheck, Check, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

function Checks({ comp }) {
  return (
    <div className="text-[12px]" data-testid="certified-details">
      <div className={`font-bold mb-1.5 ${comp.certified ? "text-emerald-700" : "text-slate-700"}`}>
        {comp.certified ? "Certified — fully compliant" : "Not yet certified"}
      </div>
      <ul className="space-y-1">
        {comp.checks.map((c) => (
          <li key={c.label} className="flex items-start gap-1.5">
            {c.ok ? <Check size={13} className="text-emerald-600 mt-0.5 shrink-0" /> : <X size={13} className="text-rose-500 mt-0.5 shrink-0" />}
            <span className={c.ok ? "text-slate-700" : "text-rose-700"}>{c.label}</span>
          </li>
        ))}
      </ul>
      {(comp.at_risk || []).length > 0 && (
        <div className="mt-2 rounded-md bg-amber-50 px-2 py-1.5 text-amber-800" data-testid="certified-at-risk">
          At risk: {comp.at_risk.map((r) => `${r.label} (${fmt(r.expiry)})`).join(", ")}
        </div>
      )}
      <div className="mt-2 pt-2 border-t border-slate-100 text-slate-500" data-testid="certified-next-expiry">
        Next expiry: <b className="text-slate-700">{comp.next_expiry ? fmt(comp.next_expiry) : "None on file"}</b>
      </div>
    </div>
  );
}

// Green tick shown next to a clinician's name when every statutory/regulatory check passes.
export default function CertifiedBadge({ comp, testid = "certified-badge", size = 15, showMissing = false, iconOnly = false, light = false }) {
  const [open, setOpen] = useState(false);
  if (!comp || (!comp.certified && !showMissing)) return null;
  const icon = <BadgeCheck size={size} className={comp.certified ? (light ? "text-emerald-300" : "text-emerald-600") : "text-slate-400"} fill={comp.certified ? (light ? "rgba(16,185,129,.25)" : "#d1fae5") : "none"} />;
  if (iconOnly) {
    return <span className="inline-flex align-middle shrink-0" title="Certified — fully compliant" data-testid={testid}>{icon}</span>;
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span role="button" tabIndex={0} data-testid={testid} aria-label={comp.certified ? "Certified" : "Not yet certified"}
          onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
          onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
          className={`inline-flex items-center gap-1 align-middle shrink-0 cursor-pointer ${comp.certified ? "" : "rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5"}`}>
          {icon}
          {!comp.certified && <span className="text-[10.5px] font-semibold text-slate-500">Not yet certified</span>}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" side="bottom" align="start" onClick={(e) => e.stopPropagation()}>
        <Checks comp={comp} />
      </PopoverContent>
    </Popover>
  );
}
